import { publicNotesQuery } from "../utils/notes/publicNotesQuery";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { getZooId, initAuth, initDb, initRealTimeDb } from "../tools/conf";
import { MANAGED_BY_V2_ERROR, isManagedByEthogramV2, withoutEthogramV2Fields } from "../ethograms-v2/managedCopy";

// Auteur d'un archivage / changement de campagne : les vues de résultat passaient
// `zoo.userId`, absent du store redux → archivedBy null (OBS-31). On retombe sur
// l'utilisateur authentifié.
const actingUserId = (userId) => userId || initAuth()?.currentUser?.uid || null;

export class obsDataConnector {
  // Copie V1 générée par les éthogrammes V2 : jamais écrite depuis le web, la recopie
  // automatique s'en charge (ADR 2026-10-23, décision 7).
  async _isManagedByEthogramV2(db, obsId) {
    if (!obsId) return false;
    const snapshot = await db.collection("obsProfile").doc(obsId).get();
    return snapshot.exists && isManagedByEthogramV2(snapshot.data());
  }

  async _getObsListFromGlobalIndex() {
    let db = initDb();
    let index = await db.collection("indexes").doc("obs").get();
    let data = index.data();

    let list = [];
    for (let key in data.index) {
      if (!data.index[key].obsName.includes("upprimer")) {
        list.push(data.index[key]);
      }
    }
    return list;
  }

  async _createObsProfile(obsData) {
    try {
      let db = initDb();
      let uuid = uuidv4();
      // Dupliquer une copie V2 donne un éthogramme V1 ordinaire, modifiable.
      let newEntry = withoutEthogramV2Fields({
        ...obsData,
        obsId: uuid,
        version: 1,
        creationDate: moment().unix(),
        lastEdit: moment().unix(),
      });
      // Un éthogramme dupliqué part sans le classement (tags, méthode, archivage)
      // de sa source : il se classe depuis la liste.
      delete newEntry.classification;
      try {
        let categoriesOptions = newEntry.categoriesOptions;
        delete newEntry.categoriesOptions;

        await db.collection("obsProfile").doc(uuid).set(newEntry);
        await db
          .collection("obsProfile")
          .doc(uuid)
          .collection("obsData")
          .doc("categoriesOptions")
          .set(categoriesOptions);

        return { success: true, data: newEntry };
      } catch (error) {
        console.error(error);
        return { success: false, data: error };
      }
    } catch (error) {
      return {
        success: false,
        data: error,
      };
    }
  }

  async _updateObsProfile(obsData) {
    try {
      let db = initDb();
      let now = moment().unix();
      let newEntry = {
        ...obsData,
        version: obsData.version + 1,
        lastEdit: now,
      };
      try {
        // Le classement (tags, méthode, archivage) se pose depuis la liste des
        // éthogrammes : l'éditeur l'a rechargé à l'ouverture et l'écraserait avec
        // une copie périmée. On reprend celui du document.
        const current = await db.collection("obsProfile").doc(newEntry.obsId).get();
        if (isManagedByEthogramV2(obsData) || (current.exists && isManagedByEthogramV2(current.data()))) {
          return { success: false, data: MANAGED_BY_V2_ERROR };
        }
        const classification = current.exists ? current.data()?.classification : undefined;
        delete newEntry.classification;
        if (classification) newEntry.classification = classification;
        await db
          .collection("obsProfile")
          .doc(newEntry.obsId)
          .collection("logs")
          .add(newEntry);
        let categoriesOptions = newEntry.categoriesOptions;
        delete newEntry.categoriesOptions;
        await db.collection("obsProfile").doc(newEntry.obsId).set(newEntry);
        await db
          .collection("obsProfile")
          .doc(newEntry.obsId)
          .collection("obsData")
          .doc("categoriesOptions")
          .set(categoriesOptions);
        return { success: true, data: newEntry };
      } catch (error) {
        console.error(error);
        return { success: false, data: error };
      }
    } catch (error) {
      return {
        success: false,
        data: error,
      };
    }
  }

  async _getSnapshotFromId(obsId, officialId) {
    try {
      let db = initDb();
      let obsData = await db
        .collection("obsProfile")
        .doc(obsId)
        .collection("officials")
        .doc(officialId)
        .get();
      let categoriesOptions = await db
        .collection("obsProfile")
        .doc(obsId)
        .collection("officials")
        .doc(officialId)
        .collection("obsData")
        .doc("categoriesOptions")
        .get();
      let data = obsData.data();
      data.categoriesOptions = categoriesOptions.data();
      return data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  async getObsFromCampaign() {
    let db = initDb();
  }

  async _createSnapshot(obsData) {
    let db = initDb();
    let officialId = uuidv4();
    let newEntry = {
      ...obsData,
      label: obsData.obsName,
      officialId: officialId,
    };

    let categoriesOptions = newEntry.categoriesOptions;
    delete newEntry.categoriesOptions;

    // Distribution applies to the parent ethogram as a whole, not to versions.
    // Strip these fields from the snapshot so they cannot drift / corrupt the index on rollback.
    delete newEntry.isPublic;
    delete newEntry.isPublicZoo;
    delete newEntry.isPublicLab;
    delete newEntry.zooAvailability;
    delete newEntry.zooAvailabilityArray;
    delete newEntry.allowedZooIds;
    // Le classement (tags, méthode, archivage) concerne l'éthogramme, pas une version.
    delete newEntry.classification;

    try {
      if (isManagedByEthogramV2(obsData) || (await this._isManagedByEthogramV2(db, newEntry.obsId))) {
        return { success: false, data: MANAGED_BY_V2_ERROR };
      }
      await db
        .collection("obsProfile")
        .doc(newEntry.obsId)
        .collection("officials")
        .doc(newEntry.officialId)
        .set(newEntry);
      await db
        .collection("obsProfile")
        .doc(newEntry.obsId)
        .collection("officials")
        .doc(newEntry.officialId)
        .collection("obsData")
        .doc("categoriesOptions")
        .set(categoriesOptions);

      return { success: true, data: newEntry };
    } catch (error) {
      console.log(error);
      return { success: false, data: error };
    }
  }

  async _getAllObsFromCampaign(campaignId) {
    let db = initDb();
    let list = [];
    try {
      let snapshot = await db
        .collection("obs")
        .where("campaignId", "==", campaignId)
        .get();
      if (snapshot.empty) {
      } else {
        snapshot.forEach((doc) => {
          let obsData = doc.data();
          if (obsData.isArchived === true) return;
          list.push({
            label: obsData.obsName,
            value: obsData.obsId,
            ...obsData,
          });
        });
        return list;
      }
    } catch (error) {
      console.log(error);
    }
    return list;
  }

  async _getSnapshots(obsId) {
    try {
      let db = initDb();
      let snaps = await db
        .collection("obsProfile")
        .doc(obsId)
        .collection("officials")
        .orderBy("officialVersion", "desc")
        .get();
      let list = [];

      snaps.docs.forEach((doc) => {
        let data = doc.data();
        data.label = `Officiel: V${data.officialVersionString || data.officialVersion}`;
        data.version = data.officialVersion;
        data.value = data.officialId;
        list.push(data);
      });

      // Trier par version complète (majeure.mineure) pour que 4.1 > 4.0
      list = list.sort((a, b) => {
        // Utiliser officialVersionString pour un tri précis (ex: "4.1" vs "4.0")
        const versionA = a.officialVersionString || `${a.officialVersion}.0`;
        const versionB = b.officialVersionString || `${b.officialVersion}.0`;

        const [majorA, minorA] = versionA.split('.').map(Number);
        const [majorB, minorB] = versionB.split('.').map(Number);

        // Comparer d'abord la version majeure
        if (majorA !== majorB) {
          return majorB - majorA; // Ordre décroissant
        }
        // Si même version majeure, comparer la version mineure
        return minorB - minorA; // Ordre décroissant
      });

      list.push({
        label: "Version courante",
        value: "current",
        officialVersion: "current",
      });
      return list;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  /**
   * Get ethograms list filtered by zoo ID and zoo type
   * @param {boolean} restricted - Whether to filter by zoo access
   * @param {string} zooId - The zoo ID to filter by
   * @param {string} zooType - The type of establishment ('zoo', 'lab', or 'projet'). Defaults to 'zoo'.
   * @returns {Promise<Array>} List of ethograms accessible to this zoo
   */
  // Helper to create obs item with searchable label
  _createObsItem(obsData) {
    // Create searchable string that includes both FR and EN names for filtering
    const searchableText = [
      obsData.obsName,
      obsData.obsInternationalName,
    ].filter(Boolean).join(' ');

    return {
      ...obsData,
      label: obsData.obsName,
      value: obsData.obsId,
      searchableText: searchableText,
    };
  }

  async _getObsList(restricted, zooId, zooType = 'zoo') {
    let db = await initDb();
    let list = [];
    try {
      if (restricted && zooId) {
        // Use a Map to avoid duplicates
        let obsMap = new Map();

        // Get zoo-specific ethograms using NEW field: allowedZooIds
        let allowedZooSnapshot = await db
          .collection("obsProfile")
          .where("allowedZooIds", "array-contains", zooId)
          .get();

        allowedZooSnapshot.forEach((doc) => {
          let obsData = doc.data();
          if (obsData.isDelete !== true) {
            obsMap.set(obsData.obsId, this._createObsItem(obsData));
          }
        });

        // RETROCOMPATIBILITY: Also check legacy field zooAvailabilityArray
        let zooSpecificSnapshot = await db
          .collection("obsProfile")
          .where("zooAvailabilityArray", "array-contains", zooId)
          .get();

        zooSpecificSnapshot.forEach((doc) => {
          let obsData = doc.data();
          if (obsData.isDelete !== true && !obsMap.has(obsData.obsId)) {
            obsMap.set(obsData.obsId, this._createObsItem(obsData));
          }
        });

        // Get public ethograms based on establishment type
        // For labs: check isPublicLab
        // For zoos: check isPublicZoo
        if (zooType === 'lab') {
          // Get ethograms with isPublicLab = true
          let publicLabSnapshot = await db
            .collection("obsProfile")
            .where("isPublicLab", "==", true)
            .get();

          publicLabSnapshot.forEach((doc) => {
            let obsData = doc.data();
            if (obsData.isDelete !== true && !obsMap.has(obsData.obsId)) {
              obsMap.set(obsData.obsId, this._createObsItem(obsData));
            }
          });
        } else {
          // For zoos and other types (projet, etc.), check isPublicZoo
          let publicZooSnapshot = await db
            .collection("obsProfile")
            .where("isPublicZoo", "==", true)
            .get();

          publicZooSnapshot.forEach((doc) => {
            let obsData = doc.data();
            if (obsData.isDelete !== true && !obsMap.has(obsData.obsId)) {
              obsMap.set(obsData.obsId, this._createObsItem(obsData));
            }
          });
        }

        // RETROCOMPATIBILITY: Also check legacy isPublic field (accessible to all)
        let publicSnapshot = await db
          .collection("obsProfile")
          .where("isPublic", "==", true)
          .get();

        publicSnapshot.forEach((doc) => {
          let obsData = doc.data();
          if (obsData.isDelete !== true && !obsMap.has(obsData.obsId)) {
            obsMap.set(obsData.obsId, this._createObsItem(obsData));
          }
        });

        // Convert map to array
        list = Array.from(obsMap.values());
      } else {
        // Get all ethograms (for admin views)
        let snapshot = await db.collection("obsProfile").get();
        if (!snapshot.empty) {
          snapshot.forEach((doc) => {
            let obsData = doc.data();
            if (obsData.isDelete !== true) {
              list.push(this._createObsItem(obsData));
            }
          });
        }
      }

      list.sort((a, b) => a.obsName.localeCompare(b.obsName));

      return list;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  async _getAllEthogramVariationFromCampaign(
    zooId,
    obsId,
    campaignId,
    tradCampaign
  ) {
    let current = await this._getObsFromId(obsId, true);
    let db = await initDb();

    let obsFromCampaign = [
      {
        ...current,
        label: `${current.obsName} - v${current.version}`,
        value: null,
      },
    ];
    // Hors campagne : seule la version courante. L'ancien appel
    // _getAllObsFromCampaign(zooId) cherchait des observations dont la campagne
    // porte l'identifiant du parc : toujours vide, et refusé par les règles
    // depuis les droits par parc (#545).
    let allcampaigns = [];
    if (campaignId) {
      let campaignData = (
        await db.collection("campaigns").doc(campaignId).get()
      ).data();
      allcampaigns = [campaignData];
    }

    await Promise.all(
      allcampaigns.map(async (campaignData) => {
        let doc = await db
          .collection("campaigns")
          .doc(campaignData.campaignId)
          .collection("obs")
          .doc("obsData")
          .get();
        let data = doc.data(); // Access the 'data' property of the document
        obsFromCampaign.push({
          ...data,
          label: `${tradCampaign} : ${campaignData.campaignName} - v${data.version}`,
          value: data.campaignId,
        });
      })
    );

    return obsFromCampaign;
  }

  async _getObsFromFilter(start, stop, selectedZoo) {
    let db = await initDb();
    let list = [];
    try {
      if (selectedZoo !== null) {
        var snapshot = await db
          .collection("obs")
          .where("obsStop", ">=", start)
          .where("zooId", "==", selectedZoo.value)
          .where("obsStop", "<=", stop)
          .get();
      } else {
        snapshot = await db
          .collection("obs")
          .where("obsStop", ">=", start)
          .where("obsStop", "<=", stop)
          .get();
      }
      if (snapshot.empty) {
      } else {
        snapshot.forEach((doc) => {
          let obsData = doc.data();
          if (obsData.isArchived === true) return;
          list.push({
            label: obsData.obsName,
            value: obsData.obsId,
            ...obsData,
          });
        });
      }

      list.sort(function (a, b) {
        if (a.obsName < b.obsName) {
          return -1;
        }
        if (a.obsName > b.firstname) {
          return 1;
        }
        return 0;
      });

      return list;
    } catch (error) {
      console.log(error);
    }
  }

  async _getObsDataFromId(obsId) {
    let db = await initDb();
    let res = await db.collection("obs").doc(obsId).get();

    return res.data();
  }

  async _getObsFromCampaignAndUser(campaignId, userId) {
    let db = await initDb();
    let list = [];
    try {
      let snapshot = await db
        .collection("obs")
        .where("campaignId", "==", campaignId)
        .where("obsUserId", "==", userId)
        .get();
      if (snapshot.empty) {
      } else {
        snapshot.forEach((doc) => {
          let obsData = doc.data();
          if (obsData.isArchived === true) return;
          list.push({
            label: obsData.obsName,
            value: obsData.obsId,
            ...obsData,
          });
        });
        return list;
      }
    } catch (error) {
      console.log(error);
    }
    return list;
  }

  async _getObsData(obs) {
    let db = initDb();
    let res = await db.collection("obs").doc(obs).get();
    let obsData = res.data();

    return obsData;
  }

  async _addNewNote(obsId, noteData) {
    let db = await initDb();
    try {
      let noteUid = uuidv4();
      noteData.noteId = noteUid;
      await db
        .collection("obsProfile")
        .doc(obsId)
        .collection("notes")
        .doc(noteUid)
        .set(noteData);
      return {
        success: true,
      };
    } catch {
      return {
        success: false,
      };
    }
  }

  async _getNotes(obsId) {
    let db = await initDb();
    try {
      let snap = await publicNotesQuery(db
        .collection("obsProfile")
        .doc(obsId)
        .collection("notes"))
        .orderBy("date")
        .get();
      let notes = [];
      snap.forEach((el) => {
        notes.push(el.data());
      });
      return notes;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  async _getObsFromId(obsId, allData) {
    let db = await initDb();
    try {
      let obsData = await (
        await db.collection("obsProfile").doc(obsId).get()
      ).data();
      if (allData) {
        let categoriesOptions = await (
          await db
            .collection("obsProfile")
            .doc(obsId)
            .collection("obsData")
            .doc("categoriesOptions")
            .get()
        ).data();
        obsData = {
          label: obsData.obsName,
          value: obsData.obsId,
          ...obsData,
          categoriesOptions: categoriesOptions,
        };
      }
      return obsData;
    } catch {
      console.log(console.error());
    }
  }

  /**
   * Met à jour le translationScore d'un éthogramme dans le document principal
   * @param {string} obsId - ID de l'éthogramme
   * @param {Object} translationScore - Score de traduction calculé
   * @returns {Promise<{success: boolean, error?: any}>}
   */
  async _updateTranslationScore(obsId, translationScore) {
    let db = initDb();
    try {
      if (await this._isManagedByEthogramV2(db, obsId)) return { success: false, data: MANAGED_BY_V2_ERROR, error: MANAGED_BY_V2_ERROR };
      await db.collection("obsProfile").doc(obsId).update({ translationScore });
      console.log(`[obsDataConnector] TranslationScore updated for obsId: ${obsId}`);
      return { success: true };
    } catch (error) {
      console.error(`[obsDataConnector] Error updating translationScore for obsId: ${obsId}`, error);
      return { success: false, error };
    }
  }

  async _deleteObs(obsId) {
    let db = initDb();
    try {
      if (await this._isManagedByEthogramV2(db, obsId)) return { success: false, data: MANAGED_BY_V2_ERROR };
      await db.collection("obsProfile").doc(obsId).update({ isDelete: true });
      return { success: true };
    } catch (error) {
      console.log(error);
      return { success: false, data: error };
    }
  }

  async _deleteObsData(obsId) {
    let db = initDb();
    try {
      await db.collection("obs").doc(obsId).delete();
      return { success: true };
    } catch (error) {
      console.log(error);
      return { success: false, data: error };
    }
  }

  async _getActiveCampaignFromObsId(obsId, zooId, now) {
    let db = await initDb();
    let snapshot;
    if (zooId !== undefined) {
      snapshot = await db
        .collection("campaigns")
        .where("obsId", "==", obsId)
        .where("zooId", "==", zooId)
        .where("campaignEndDate", ">", now)
        .get();
    } else {
      snapshot = await db
        .collection("campaigns")
        .where("obsId", "==", obsId)
        .where("campaignEndDate", ">", now)
        .get();
    }

    let activeCampaign = [];
    snapshot.forEach((doc) => {
      let campaign = doc.data();
      activeCampaign.push({
        campaignName: campaign.campaignName,
        campaignId: campaign.campaignId,
        zooName: campaign.zooName,
        obsVersion: campaign.obsVersion,
      });
    });
    return activeCampaign;
  }

  getUserNameFromId(userId, userList) {
    let user = userList.find((el) => el.value === userId);
    if (user) {
      return user.label;
    } else {
      return "Utilisateur non identifié";
    }
  }

  async _getAllObsFromPeriodAndAnimalId(start, stop, animalId) {
    try {
      let db = await initDb();
      let snapshot = await db
        .collection("obs")
        .where("obsStop", ">=", start)
        .where("obsStop", "<=", stop)
        .where("animalId", "==", animalId)
        .get();
      let obs = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.isArchived === true) return;
        obs.push(data);
      });

      return obs;
    } catch (error) {
      console.log(error);
    }
  }

  async _getallObsFromPeriod(start, stop, activeUserList) {
    let realtime = await initRealTimeDb();
    let zooId = await getZooId();

    // Fonction pour générer la liste des dates formatées "DDMMYYYY" entre start et stop
    let getEveryDayFromPeriod = (start, stop) => {
      let days = [];
      let day = moment.unix(start);
      while (day <= moment.unix(stop)) {
        let formattedDate = day.format("DDMMYYYY");
        days.push(formattedDate);
        day = day.add(1, "day");
      }
      return days;
    };

    let days = getEveryDayFromPeriod(start, stop);

    // Pour chaque jour, on lance la requête Firebase en parallèle
    const dayPromises = days.map(async (day) => {
      let obsSnapshot = await realtime
        .ref("companies")
        .child(zooId)
        .child("obs")
        .child(day)
        .once("value");
      let obsDataDayData = obsSnapshot.val();

      // Variables locales pour stocker les données du jour
      let dayObs = [];
      let dayObsCountByUserName = {};
      let dayObsCountBySpecie = {};

      if (obsDataDayData) {
        for (let key in obsDataDayData) {
          let obsData = obsDataDayData[key];

          if (obsData.isArchived === true) continue;

          let userId = obsData.obsUserId;
          // Si l'utilisateur n'est pas présent dans la liste active, on ignore cette observation
          if (!activeUserList.some((user) => user.value === userId)) {
            continue;
          }

          // Récupération du nom de l'utilisateur
          let userName = this.getUserNameFromId(userId, activeUserList);

          // Comptabilisation par espèce
          if (!dayObsCountBySpecie[obsData.specieId]) {
            dayObsCountBySpecie[obsData.specieId] = {};
          }
          dayObsCountBySpecie[obsData.specieId][key] = obsData;

          // Ajout de l'observation dans le tableau du jour
          dayObs.push(obsData);

          // Comptabilisation par utilisateur
          dayObsCountByUserName[userName] =
            (dayObsCountByUserName[userName] || 0) + 1;
        }
      }

      return {
        dayObs,
        dayObsCountByUserName,
        dayObsCountBySpecie,
      };
    });

    // Exécution de toutes les requêtes en parallèle
    let results = await Promise.all(dayPromises);

    // Agrégation des résultats de toutes les journées
    let obs = [];
    let obsCountByUserName = {};
    let obsCountBySpecie = {};

    results.forEach((result) => {
      obs = obs.concat(result.dayObs);
      for (let userName in result.dayObsCountByUserName) {
        obsCountByUserName[userName] =
          (obsCountByUserName[userName] || 0) +
          result.dayObsCountByUserName[userName];
      }
      for (let specieId in result.dayObsCountBySpecie) {
        if (!obsCountBySpecie[specieId]) {
          obsCountBySpecie[specieId] = {};
        }
        Object.assign(
          obsCountBySpecie[specieId],
          result.dayObsCountBySpecie[specieId]
        );
      }
    });

    return {
      obs: obs,
      obsCountByUserName: obsCountByUserName,
      obsCountBySpecie: obsCountBySpecie,
    };
  }

  // ── Archive / Unarchive / Reassign ──

  async _archiveObs(obsDocId, userId, reason) {
    let db = initDb();
    try {
      await db.collection("obs").doc(obsDocId).update({
        isArchived: true,
        archivedAt: new Date(),
        archivedBy: actingUserId(userId),
        archiveReason: reason || null,
      });
      return { success: true };
    } catch (error) {
      console.error("[obsDataConnector] _archiveObs error:", error);
      return { success: false, error };
    }
  }

  async _unarchiveObs(obsDocId) {
    let db = initDb();
    try {
      await db.collection("obs").doc(obsDocId).update({
        isArchived: false,
        archivedAt: null,
        archivedBy: null,
        archiveReason: null,
      });
      return { success: true };
    } catch (error) {
      console.error("[obsDataConnector] _unarchiveObs error:", error);
      return { success: false, error };
    }
  }

  async _updateObsCampaign(obsDocId, newCampaignId, userId) {
    let db = initDb();
    try {
      let obsDoc = await db.collection("obs").doc(obsDocId).get();
      let oldCampaignId = obsDoc.exists ? obsDoc.data().campaignId : null;

      await db.collection("obs").doc(obsDocId).update({
        campaignId: newCampaignId,
        previousCampaignId: oldCampaignId,
        campaignReassignedAt: new Date(),
        campaignReassignedBy: actingUserId(userId),
      });
      return { success: true };
    } catch (error) {
      console.error("[obsDataConnector] _updateObsCampaign error:", error);
      return { success: false, error };
    }
  }
}
